import React, { useState, useContext, useEffect } from 'react';

// Styles & Assets
import styles from '../styles/Columns.module.css';

// Services
import { findMatchingRule, getHueFromHex } from '../services/Scores';

// Context
import ColumnsContext from './ColumnsContext';

// Components
import ScoreCircle from '../components/UI/ScoreCircle/ScoreCircle';

// Interfaces
import { Score, IScoreRule } from '../interfaces/Score';
import { Patient } from '../interfaces/Patient';

const ScoresContext = React.createContext<{
    scores: Score[] | null, setScores: React.Dispatch<React.SetStateAction<Score[] | null>>,
}>({
    scores: [], setScores: () => undefined,
});

export const ScoresContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const { dispatchColumns } = useContext(ColumnsContext);

    const [scores, setScores] = useState<Score[] | null>(null);

    const sortIndicator = (
        rules: IScoreRule[],
        lastScore: number | undefined,
        secondLastScore: number | undefined,
    ) => {
        const matchRule = findMatchingRule(rules, lastScore);
        if (lastScore === undefined || matchRule === null) return '1000';
        let score = -getHueFromHex(matchRule.color_alert);
        if (secondLastScore === undefined) return score.toString();
        score += secondLastScore < lastScore ? 0.1 :
            secondLastScore === lastScore ? 0.2 : 0.3;
        return score.toString();
    };

    useEffect(() => {
        if (scores === null || scores.length === 0) return;
        const columns = scores.map(sc => ({
            id: 1000 + sc.id,
            name: () => sc.name || '',
            sortable: true,
            selectableCell: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            element: (patient: Patient) => patient.cases[0] ?
                <ScoreCircle
                    rules={sc.score_rules}
                    lastScore={patient.scores?.find(score => score.score_id === sc.id)?.lastScore}
                    secondLastScore={patient.scores?.find(score => score.score_id === sc.id)?.secondLastScore}
                /> : <></>,
            sortElementFunc: (patient: Patient) => {
                const computedScore = patient.scores?.find(score => score.score_id === sc.id);
                return sortIndicator(sc.score_rules, computedScore?.lastScore, computedScore?.secondLastScore);
            }
        }));
        dispatchColumns({ type: 'ADD_SCORE_COLUMNS', columns });
    }, [dispatchColumns, scores]);

    const initState = { scores, setScores };

    return (
        <ScoresContext.Provider value={initState}>
            {children}
        </ScoresContext.Provider>
    );
};

export default ScoresContext;